import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { StatusCode, VGError } from 'src/app/utilities/VGError';
import { VlApiService } from '../vl-api/vl-api.service';
import { map } from 'rxjs/operators';
import { SOCKET_KIND_NOTIFICATION, SocketIOService, SocketNotification, SocketResourceParameterNotification } from '../socketio/socketio.service';

export interface Populate {
    path: string;
    populate?: Populate;
}

export interface PopulateRequest {
    populate?: Populate;
    populateDataType?: boolean;
    populateAllParametersMeta?: boolean;
    populateFileMedias?: boolean;
    fieldParameters?: string[];
    currencyId?: boolean;
    countryId?: boolean;
}

export interface PaginationResult<T> {
    currentPage: number;
    totalItems: number;
    totalPages: number;
    results: T[];
}

export interface Pagination {
    limit: number;
    startIndex: number;
    sort?: string[];
    filter?: any;
    search?: object | string;
    skipCache?: boolean;
}


export interface GetConfigRequest {
    populateRequest?: PopulateRequest,
    pagination?: Pagination,
    cacheHeader?: boolean;
    query?: any;
}

export function deleteSystemVariable(value: any) {
    delete value['created_at'];
    delete value['updated_at'];
    delete value['__v'];

    /** Parse parameter as object value array */
    if (value['parameters']) {
        let parameters = Object.values(value['parameters']);

        if (parameters && parameters.length) {
            parameters.forEach(parameter => {
                // Delete historic and system variabls
                delete parameter['historic']
                delete parameter['created_at'];
                delete parameter['updated_at'];
            });
        }
    }
}

interface CrudMethod {
    create: boolean;
    update: boolean;
    delete: boolean;
    get: boolean;
    getAll: boolean;
}

@Injectable({
    providedIn: 'root'
})


export abstract class AbstractService<U> {
    abstract route: string;
    abstract resourceReferenceLabel: string;
    protected specificSocketMessages: Map<string, Subject<any>>;

    protected crudMethods: CrudMethod = {
        create: false,
        update: false,
        delete: false,
        get: false,
        getAll: false
    }

    protected cacheMethods: { get: boolean, getAll: boolean } = {
        get: false,
        getAll: false
    }

    private rooms: Map<string, number> = new Map<string, number>();
    private onParameterUpdated$: Subject<SocketResourceParameterNotification> = new Subject();

    get onParameterUpdated(): Observable<SocketResourceParameterNotification> {
        return this.onParameterUpdated$.asObservable();
    }

    constructor(protected vlapi: VlApiService, private socketIOService: SocketIOService) {
        setTimeout(() => {
            this.socketIOService.subject
                ?.subscribe((message: SocketResourceParameterNotification & SocketNotification) => {
                    const specificMessage: string[] = this.specificSocketMessages ? Array.from(this.specificSocketMessages?.keys()) : [];

                    if (specificMessage.includes(message.kind)) {
                        this.specificSocketMessages.get(message.kind).next(message);
                    } else {
                        if (!message.resourceReferenceLabel || message.resourceReferenceLabel != this.resourceReferenceLabel) {
                            return;
                        }

                        switch (message.kind) {
                            case SOCKET_KIND_NOTIFICATION.RESOURCE_PARAMETER_NOTIFICATION:
                                this.vlapi.invalidateEndpointRequest(this.route);

                                this.rooms.forEach((count: number, room: string) => {
                                    const [resouceName, id] = room.split(':');
                                    if (id != null) {
                                        this.vlapi.invalidateEndpointRequest(this.route + id);
                                    }
                                });

                                this.onParameterUpdated$.next(message);
                                break;
                        }
                    }
                });
        });
    }

    public abstract validate(value: U, ...args: any): void;

    public joinSocketRoom(id: string): void {
        if (!id) {
            return;
        }

        const roomName: string = `${this.resourceReferenceLabel}:${id}`;

        if (this.rooms.has(roomName)) {
            this.rooms.set(roomName, this.rooms.get(roomName) + 1);
            return;
        }

        this.rooms.set(roomName, 1);

        this.socketIOService.joinRoom(roomName)
            .then(() => {
                //const count = this.rooms.get(roomName) - 1;

                // if (count > 0) {
                //   this.rooms.set(roomName, count);
                // }
            })
            .catch((error: any) => {
                const count = this.rooms.get(roomName) - 1;
                if (count > 0) {
                    this.rooms.set(roomName, count);
                }

                // TODO: sentry
            });
    }

    public leaveAllRoom(): void {
        this.rooms.forEach((value: number, roomName: string) => {
            for (let i = 0; i < value; i++) {
                this.socketIOService.leaveRoom(roomName);
            }
        });
    }

    public leaveSocketRoom(id: string): void {
        if (!id) {
            return;
        }

        const roomName = `${this.resourceReferenceLabel}:${id}`;
        if (!this.rooms.has(roomName)) {
            return;
        }

        const count = this.rooms.get(roomName) - 1;

        if (count > 0) {
            this.rooms.set(roomName, count);
            return;
        }

        this.socketIOService.leaveRoom(`${this.resourceReferenceLabel}:${id}`);
        this.rooms.delete(roomName);
    }

    public invalidGet(id: string): void {
        this.vlapi.invalidateEndpointRequest(this.route + id);
    }

    public invalidGetAll(): void {
        this.vlapi.invalidateEndpointRequest(this.route);
    }

    /**
     * Create request
     * 
     * @param value  Parsed value
     */
    public create(value: U | any, ...arg: any): Observable<string> {
        if (!this.crudMethods.create) {
            throw new Error($localize`:@@error.method-not-allowed:Method not allowed`);
        }

        deleteSystemVariable(value);
        return this.vlapi.post<string>(this.route, value)
    }

    /**
     * Update request
     * 
     * @param value  Parsed value
     */
    public update(value: U | any, ...args: U | any): Observable<U> {
        if (!this.crudMethods.update) {
            throw new Error($localize`:@@error.method-not-allowed:Method not allowed`);
        }

        deleteSystemVariable(value);

        let route: string = this.route + value._id;
        return this.vlapi.patch<U>(route, value)
    }

    /**
     * Delete request
     * 
     * @param value  Parsed value
     */
    public delete(value: U | any, ...args: U | any): Observable<U> {
        if (!this.crudMethods.delete) {
            throw new Error($localize`:@@error.method-not-allowed:Method not allowed`);
        }

        let route: string = this.route + value._id;
        return this.vlapi.delete<U>(route)
    }

    /**
     * Get request
     * 
     * @param value             ID of the item
     * @param populateRequest   Populate request
     */
    public get(id: string, config?: GetConfigRequest): Observable<U> {
        if (!this.crudMethods.get) {
            throw new Error($localize`:@@error.method-not-allowed:Method not allowed`);
        }

        let vlApiConfig = {
            populateRequest: config?.populateRequest,
            paramsRequest: config?.pagination,
            cache: this.cacheMethods.get
        }

        return this.vlapi.get<U>(this.route + id, vlApiConfig)
    }

    /**
     * Get request
     * 
     * @param value             ID of the item
     * @param populateRequest   Populate request
     * @param Pagination        Pagination
     */
    public getAll(config?: GetConfigRequest): Observable<PaginationResult<U>> {
        if (!this.crudMethods.getAll) {
            throw new Error($localize`:@@error.method-not-allowed:Method not allowed`);
        }

        let vlApiConfig = {
            populateRequest: config?.populateRequest,
            paramsRequest: config?.pagination,
            query: config?.query,
            cache: this.cacheMethods.getAll
        }

        return this.vlapi.get<PaginationResult<U>>(this.route, vlApiConfig)
    }
}